import { pointInPolygon } from './roomShape.js';
/** App setting holding the office's aisle, in feet. */
export const FLOOR_PLAN_CLEARANCE_SETTING = 'floor_plan_min_clearance_ft';
/** A guest walking between two seated tables needs about three feet. */
export const DEFAULT_MIN_CLEARANCE_FT = 3;
/** Clearance is at least this, and at most `MAX_MIN_CLEARANCE_FT`. */
export const MIN_MIN_CLEARANCE_FT = 0;
export const MAX_MIN_CLEARANCE_FT = 20;
/** Below this, two outlines are touching. */
const TOUCH_FT = 1e-3;
// ─── Outlines ───────────────────────────────────────────────────────
/** Rotate points about a centre, clockwise in plan (y down) for positive degrees. */
export function rotatePoints(points, cx, cy, deg) {
    if (!deg)
        return points;
    const rad = (deg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return points.map((p) => {
        const dx = p.x - cx;
        const dy = p.y - cy;
        return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
    });
}
/** A rectangle from its top-left before rotation, turned about its centre. */
export function rectFootprint(x, y, width, height, rotationDeg = 0) {
    const corners = [
        { x, y },
        { x: x + width, y },
        { x: x + width, y: y + height },
        { x, y: y + height },
    ];
    return rotatePoints(corners, x + width / 2, y + height / 2, rotationDeg);
}
/** An ellipse filling the same box, as a polygon of `steps` corners. */
export function ellipseFootprint(x, y, width, height, rotationDeg = 0, steps = 24) {
    const cx = x + width / 2;
    const cy = y + height / 2;
    // Corners on the circle would cut inside it; pushing them out by the
    // polygon's inset keeps the outline from under-reporting an overlap.
    const grow = 1 / Math.cos(Math.PI / steps);
    const pts = [];
    for (let i = 0; i < steps; i++) {
        const t = (i / steps) * Math.PI * 2;
        pts.push({
            x: cx + (width / 2) * grow * Math.cos(t),
            y: cy + (height / 2) * grow * Math.sin(t),
        });
    }
    return rotatePoints(pts, cx, cy, rotationDeg);
}
// ─── Distances ──────────────────────────────────────────────────────
function pointSegmentDistance(p, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}
const cross = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
/** The two segments cross at a point inside both — not merely touching. */
function segmentsCrossProperly(a, b, c, d) {
    const eps = 1e-9;
    const d1 = cross(c, d, a);
    const d2 = cross(c, d, b);
    const d3 = cross(a, b, c);
    const d4 = cross(a, b, d);
    return (((d1 > eps && d2 < -eps) || (d1 < -eps && d2 > eps)) &&
        ((d3 > eps && d4 < -eps) || (d3 < -eps && d4 > eps)));
}
/** Shortest distance between two segments (0 when they touch or cross). */
export function segmentDistance(a, b, c, d) {
    if (segmentsCrossProperly(a, b, c, d))
        return 0;
    return Math.min(pointSegmentDistance(a, c, d), pointSegmentDistance(b, c, d), pointSegmentDistance(c, a, b), pointSegmentDistance(d, a, b));
}
function boundaryDistance(p, poly) {
    let best = Infinity;
    for (let i = 0; i < poly.length; i++) {
        best = Math.min(best, pointSegmentDistance(p, poly[i], poly[(i + 1) % poly.length]));
    }
    return best;
}
/** Inside and clear of the boundary: a point on an edge is only touching. */
function strictlyInside(p, poly) {
    return pointInPolygon(p, poly) && boundaryDistance(p, poly) > TOUCH_FT;
}
/** How far inside an edge its sample points sit — an eighth of an inch. */
const INSET_FT = 0.01;
/**
 * Points just inside the outline along each edge. Two outlines that share
 * part of an edge line (a table slid half its width along another) cross no
 * edges and put no corner inside each other, yet cover the same floor; a
 * point a hair inside one of them lands well inside the other.
 */
function insideSamples(points) {
    const out = [];
    for (let i = 0; i < points.length; i++) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        const len = Math.hypot(b.x - a.x, b.y - a.y);
        if (len === 0)
            continue;
        const nx = (-(b.y - a.y) / len) * INSET_FT;
        const ny = ((b.x - a.x) / len) * INSET_FT;
        for (const t of [0.25, 0.5, 0.75]) {
            const mx = a.x + (b.x - a.x) * t;
            const my = a.y + (b.y - a.y) * t;
            // Whichever side of the edge is the outline's own inside, whatever
            // order its corners were drawn in.
            const left = { x: mx + nx, y: my + ny };
            out.push(pointInPolygon(left, points) ? left : { x: mx - nx, y: my - ny });
        }
    }
    return out;
}
/** The two outlines cover some of the same floor (touching does not count). */
export function polygonsOverlap(p, q) {
    if (p.length < 3 || q.length < 3)
        return false;
    for (let i = 0; i < p.length; i++) {
        for (let j = 0; j < q.length; j++) {
            if (segmentsCrossProperly(p[i], p[(i + 1) % p.length], q[j], q[(j + 1) % q.length])) {
                return true;
            }
        }
    }
    // No edges cross: apart, touching, or one covering part of the other along
    // shared lines. A corner, or a point just inside an edge, of one lying well
    // inside the other settles it.
    const inside = (pts, poly) => pts.some((v) => strictlyInside(v, poly));
    return inside(p, q) || inside(q, p) || inside(insideSamples(p), q) || inside(insideSamples(q), p);
}
/** Gap between two outlines in feet; 0 when they touch or overlap. */
export function polygonGap(p, q) {
    if (polygonsOverlap(p, q))
        return 0;
    let best = Infinity;
    for (let i = 0; i < p.length; i++) {
        for (let j = 0; j < q.length; j++) {
            best = Math.min(best, segmentDistance(p[i], p[(i + 1) % p.length], q[j], q[(j + 1) % q.length]));
        }
    }
    return best;
}
function boxOf(points) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of points) {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
    }
    return { minX, minY, maxX, maxY };
}
/** Clearance a caller may pass, held inside the supported range. */
export function clampClearance(ft) {
    const n = typeof ft === 'number' ? ft : Number(ft);
    if (!Number.isFinite(n))
        return DEFAULT_MIN_CLEARANCE_FT;
    return Math.min(MAX_MIN_CLEARANCE_FT, Math.max(MIN_MIN_CLEARANCE_FT, n));
}
/**
 * Every pair of footprints from different clusters that overlaps, or stands
 * closer than `clearanceFt`. Overlaps come first, then the tightest gaps.
 */
export function spacingIssues(footprints, clearanceFt) {
    const clearance = clampClearance(clearanceFt);
    const usable = footprints.filter((f) => f.points.length >= 3);
    const boxes = usable.map((f) => boxOf(f.points));
    const issues = [];
    // Sweep along x: with the boxes in order of their left edge, each one is
    // compared only with those starting before its right edge plus the
    // clearance. Every item on the plan — chairs included — is checked on each
    // edit, so this keeps it near linear for a spread-out room instead of
    // comparing every pair.
    const order = usable.map((_, i) => i).sort((p, q) => boxes[p].minX - boxes[q].minX);
    for (let oi = 0; oi < order.length; oi++) {
        const ia = order[oi];
        for (let oj = oi + 1; oj < order.length; oj++) {
            const ib = order[oj];
            if (boxes[ib].minX - boxes[ia].maxX >= clearance)
                break;
            const [i, j] = ia < ib ? [ia, ib] : [ib, ia];
            const a = usable[i];
            const b = usable[j];
            if (a.cluster === b.cluster)
                continue;
            const ba = boxes[i];
            const bb = boxes[j];
            // Boxes further apart than the clearance cannot hold a closer pair.
            if (ba.minY - bb.maxY >= clearance || bb.minY - ba.maxY >= clearance)
                continue;
            if (polygonsOverlap(a.points, b.points)) {
                issues.push({ a: a.key, b: b.key, kind: 'overlap', gapFt: 0 });
                continue;
            }
            const gap = polygonGap(a.points, b.points);
            const aisle = a.needsClearance !== false || b.needsClearance !== false;
            if (aisle && gap > TOUCH_FT && gap < clearance) {
                issues.push({ a: a.key, b: b.key, kind: 'tight', gapFt: Math.round(gap * 100) / 100 });
            }
        }
    }
    return issues.sort((x, y) => x.kind === y.kind ? x.gapFt - y.gapFt : x.kind === 'overlap' ? -1 : 1);
}
//# sourceMappingURL=floorSpacing.js.map